import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBars,
  FaTimes,
  FaHome,
  FaBriefcase,
  FaLayerGroup,
  FaCar,
  FaFolderOpen,
  FaEnvelope,
  FaCut
} from 'react-icons/fa';
import '../css/Header.css';

const navItems = [
  { id: 'hero', label: 'Home', icon: <FaHome /> },
  { id: 'experience', label: 'Experience', icon: <FaBriefcase /> },
  { id: 'skills', label: 'Skills', icon: <FaLayerGroup /> },
  { id: 'mechanic-finders', label: 'Mechanic Finders', icon: <FaCar /> },
  { id: 'cutcut-barbershop', label: 'CutCut', icon: <FaCut /> },
  { id: 'projects', label: 'Projects', icon: <FaFolderOpen /> },
  { id: 'contact', label: 'Contact', icon: <FaEnvelope /> }
];

const HEADER_OFFSET = 96;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const sectionIds = useMemo(() => navItems.map((item) => item.id), []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const y =
      element.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;

    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });

    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 16);

      const marker = HEADER_OFFSET + 30;
      let current = 'hero';

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();

        if (rect.top <= marker && rect.bottom >= marker) {
          current = id;
          break;
        }
      }

      setActiveSection(current);
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [sectionIds]);

  useEffect(() => {
    const closeOnResize = () => {
      if (window.innerWidth > 980) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', closeOnResize);
    return () => window.removeEventListener('resize', closeOnResize);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      if (!el) return;

      const y =
        el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;

      window.scrollTo({
        top: y,
        behavior: 'auto'
      });
    }, 120);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <motion.header
        className={`header-shell ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -48, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="header-glow" />
        <div className="header-grid" />

        <div className="header-inner">
          <button
            type="button"
            className="brand-wrap"
            onClick={() => scrollToSection('hero')}
            aria-label="Go to home"
          >
            <div className="brand-orb-wrap">
              <div className="brand-orb-core" />
              <div className="brand-orb-ring" />
            </div>

            <div className="brand-text">
              <span className="brand-name">Onur Korkmaz</span>
              <span className="brand-role">Full Stack • AI • Mechanic Finders</span>
            </div>
          </button>

          <nav className="desktop-nav" aria-label="Main navigation">
            <ul>
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`nav-pill ${
                      activeSection === item.id ? 'active' : ''
                    }`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <span className="nav-pill-icon">{item.icon}</span>
                    <span className="nav-pill-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            className={`mobile-menu-btn ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="mobile-nav-panel"
              initial={{ y: -14, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -10, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
            >
              <div className="mobile-nav-top">
                <div className="mobile-panel-title">Navigation</div>

                <button
                  type="button"
                  className="mobile-close-btn"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mobile-nav-links">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    type="button"
                    className={`mobile-nav-link ${
                      activeSection === item.id ? 'active' : ''
                    }`}
                    onClick={() => scrollToSection(item.id)}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ delay: index * 0.04, duration: 0.2 }}
                  >
                    <span className="mobile-nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}